import { Modal, Form } from "react-bootstrap";
import { ServerRequestsSubscriptionGroupContractRuleDto, ServerRequestsSubscriptionGroupsResponse } from "./shared/api/axios-client";
import { useEffect, useMemo, useState } from "react";
import { Button } from "./Controls/Button";

type GroupWithContract = Pick<
  ServerRequestsSubscriptionGroupsResponse,
  "id" | "name" | "contractRules"
>;

type Props = {
  show: boolean;
  onHide: () => void;
  /** One or more groups to browse. */
  groups: GroupWithContract[];
  /** Prefer this group when the dialog opens. */
  initialGroupId?: number | null;
  /** Plan monthly unit price in cents — used to resolve final tier rates. */
  basePriceCents: number;
  onAddContract?: (groupId: number) => void;
  onEditContract?: (group: GroupWithContract) => void;
};

function formatCurrency(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

/** rule.price is signed adjustment (fee +, discount −); 0 ⇒ Free. */
function getFinalPriceCents(
  adjustment: number | null | undefined,
  _type: number | null | undefined,
  basePriceCents: number,
): number {
  if (adjustment == null || adjustment === 0) return 0;
  return Math.max(basePriceCents + adjustment, 0);
}

export default function ContractDetailsModal({
  show,
  onHide,
  groups,
  initialGroupId,
  basePriceCents,
  onAddContract,
  onEditContract,
}: Props) {
  const selectable = useMemo(
    () => groups.filter((g) => g.id != null),
    [groups],
  );

  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!show || selectable.length === 0) {
      setSelectedId(null);
      return;
    }
    const preferred =
      initialGroupId != null && selectable.some((g) => g.id === initialGroupId)
        ? initialGroupId
        : selectable[0]!.id!;
    setSelectedId(preferred);
  }, [show, initialGroupId, selectable]);

  const selected = selectable.find((g) => g.id === selectedId);
  const contract = selected?.contractRules ?? [];
  const hasContract = contract.length > 0;

  const sortedContract = useMemo(() => {
    return [...contract].sort((a, b) => {
      const aMin = a.min ?? Number.MAX_SAFE_INTEGER;
      const bMin = b.min ?? Number.MAX_SAFE_INTEGER;
      return aMin - bMin;
    });
  }, [contract]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Contract Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {selectable.length === 0 ? (
          <div className="text-muted small">No groups on this plan yet.</div>
        ) : (
          <>
            {selectable.length > 1 && (
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted mb-1">Group</Form.Label>
                <Form.Select
                  value={selectedId ?? ""}
                  onChange={(e) => setSelectedId(parseInt(e.target.value, 10))}
                >
                  {selectable.map((g) => (
                    <option key={g.id} value={g.id!}>
                      {g.name}
                      {(g.contractRules?.length ?? 0) > 0 ? "" : " (no contract)"}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <div className="mb-3">
              {hasContract ? (
                <div className="text-muted small">
                  Pricing rules for <strong>{selected?.name}</strong>
                </div>
              ) : null}
            </div>

            {hasContract ? (
              <>
                <div className="fw-semibold mb-2">Tier Rules</div>
                <div className="d-flex flex-column gap-2">
                  {sortedContract.map((r: ServerRequestsSubscriptionGroupContractRuleDto, i: number) => {
                    const finalCents = getFinalPriceCents(
                      r.price,
                      r.type,
                      basePriceCents,
                    );
                    const isFree = finalCents === 0;

                    return (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between px-3 py-2 rounded border"
                      >
                        <div>
                          <div className="fw-medium">
                            {r.min ?? 0} – {r.max ?? "∞"} parkers
                          </div>
                          <div className="text-muted small">
                            {isFree ? "Free" : "Per parker / month"}
                          </div>
                        </div>
                        {isFree ? (
                          <span className="text-muted fw-semibold" style={{ fontSize: "1rem" }}>
                            Free
                          </span>
                        ) : (
                          <span
                            className="fw-bold text-dark"
                            style={{ fontSize: "1.05rem", letterSpacing: "0.01em" }}
                          >
                            {formatCurrency(finalCents)}
                            <span className="fw-semibold text-muted ms-1" style={{ fontSize: "0.85rem" }}>
                              / mo
                            </span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center text-center py-4">
                <div
                  className="rounded-circle mb-3"
                  style={{
                    width: 72,
                    height: 72,
                    backgroundColor: "rgba(220, 53, 69, 0.15)",
                  }}
                />
                <p className="text-secondary mb-0">
                  {selected?.name
                    ? `${selected.name} doesn't have a contract yet`
                    : "No contract yet"}
                </p>
                <p className="text-muted small mb-4">
                  Add tier pricing rules for this group
                </p>
                {onAddContract && selected?.id != null && (
                  <Button
                    className="btn-main px-4"
                    onClick={() => {
                      onAddContract(selected.id!);
                      onHide();
                    }}
                  >
                    Add Contract
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      {hasContract && onEditContract && selected?.id != null ? (
        <Modal.Footer>
          <Button
            className="btn-main"
            onClick={() => {
              onEditContract(selected);
              onHide();
            }}
          >
            Edit Contract
          </Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
}
